// OpenDork — Nostr-native terminal AI workspace

import { NostrLoginProvider } from "@nostrify/react/login";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createHead, UnheadProvider } from "@unhead/react/client";
import { AppProvider } from "@/components/AppProvider";
import NostrProvider from "@/components/NostrProvider";
import { WorkspaceProvider } from "@/hooks/useWorkspace";
import { VaultProvider } from "@/hooks/useVault";
import { useNsecPasteGuard } from "@/hooks/useNsecPasteGuard";
import type { AppConfig } from "@/contexts/AppContext";
import { APP_RELAYS } from "@/lib/appRelays";
import { loginStorage } from "@/lib/secureStorage";
import AppRouter from "./AppRouter";

const head = createHead({});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 60000,
      gcTime: 300000,
    },
  },
});

/**
 * Cold-start config. Relay pool starts on app bootstrap relays until the user
 * logs in and we pull their NIP-65 list (or they edit Settings → Relays).
 */
const defaultConfig: AppConfig = {
  appName: "OpenDork",
  appId: "opendork",
  shareOrigin: import.meta.env.VITE_SHARE_ORIGIN || undefined,
  theme: "dark",
  relayMetadata: {
    relays: APP_RELAYS.relays.map((r) => ({ ...r })),
    updatedAt: 0,
  },
  blossomServers: [],
  corsProxy: "https://proxy.shakespeare.diy/?url={href}",
  aiProviders: [],
};

export function App() {
  useNsecPasteGuard();

  return (
    <UnheadProvider head={head}>
      <AppProvider storageKey="nostr:app-config" defaultConfig={defaultConfig}>
        <QueryClientProvider client={queryClient}>
          <NostrLoginProvider storageKey="nostr:login" storage={loginStorage}>
            <NostrProvider>
              <VaultProvider>
                <WorkspaceProvider>
                  <AppRouter />
                </WorkspaceProvider>
              </VaultProvider>
            </NostrProvider>
          </NostrLoginProvider>
        </QueryClientProvider>
      </AppProvider>
    </UnheadProvider>
  );
}

export default App;
